import React, { useState } from "react";
import { currencySymbol } from "shared/CurrencySymbol";
import { BookingStatus, getAmountDue } from "../bookingStatus";

interface Props {
  booking: any;
  status: BookingStatus;
  isHost: boolean;
  isDeleting: boolean;
  isCanceling: boolean;
  onIssueInvoice: () => void; // host: opens existing <Invoice /> drawer
  onViewInvoice: () => void; // guest: opens existing <InvoiceUser /> modal
  onDeleteInvoice: () => void;
  onCancel: () => void;
  onMessage: () => void; // switch to Messages tab
}

const primary =
  "inline-flex min-h-[48px] items-center justify-center rounded-xl bg-[#C2410C] px-6 text-[15px] font-semibold text-white hover:bg-[#9A3412] disabled:opacity-50 transition-colors";
const secondary =
  "inline-flex min-h-[48px] items-center justify-center rounded-xl border border-neutral-300 dark:border-neutral-600 px-5 text-[15px] font-medium text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50 transition-colors";
const dangerLink =
  "min-h-[44px] text-sm font-medium text-red-700 dark:text-red-400 hover:underline disabled:opacity-50";

interface Copy {
  title: string;
  body?: string;
  tone?: "default" | "danger" | "success";
}

const NextStepCard: React.FC<Props> = (p) => {
  const { status, isHost } = p;
  const [confirming, setConfirming] = useState<"cancel" | "delete" | null>(null);
  const amount = `${currencySymbol()}${getAmountDue(p.booking).toFixed(2)}`;
  const invoiceNo = p.booking?.invoiceNo ?? p.booking?.invoice?.id;
  const guest = p.booking?.request_by ? String(p.booking.request_by).split(" ")[0] : "the guest";

  const copy: Copy = (() => {
    switch (status) {
      case "booking_request":
        return isHost
          ? { title: `Review ${guest}'s request and issue an invoice`, body: "The guest can pay once you send the invoice." }
          : { title: "Waiting for the host to respond", body: "You'll be notified as soon as the host sends an invoice." };
      case "invoice_sent":
        return isHost
          ? { title: "Waiting for the guest to pay", body: `Invoice sent for ${amount}.` }
          : { title: "Pay the first month to confirm your booking" };
      case "payment_received":
        return isHost
          ? { title: "Payment received", body: "The booking will be confirmed shortly." } // TODO confirm host flow
          : { title: "Payment received", body: "Your booking will be confirmed shortly." };
      case "confirmed": // not rendered for confirmed bookings (page shows payment + review cards)
        return { title: "Booking confirmed", tone: "success" };
      case "canceled":
        return { title: "This booking was canceled", body: "You can still message the other party.", tone: "danger" };
    }
  })();

  const box =
    copy.tone === "danger"
      ? "border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30"
      : copy.tone === "success"
      ? "border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/30"
      : "border-[#C2410C] dark:border-[#FF8A6E] bg-[#FFF8F3] dark:bg-[#241512]";

  const canCancel = !isHost && (status === "booking_request" || status === "invoice_sent");

  return (
    <section aria-labelledby="next-step" className={`rounded-2xl border p-5 sm:p-7 ${box}`}>
      <p className="mb-1 text-sm font-semibold text-[#C2410C] dark:text-[#FF8A6E]">Next step</p>
      <h2 id="next-step" className="text-xl font-semibold text-neutral-900 dark:text-white">{copy.title}</h2>
      {copy.body && <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">{copy.body}</p>}

      {!isHost && status === "invoice_sent" && (
        <div className="mt-4">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Amount due</p>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-4xl font-semibold text-neutral-900 dark:text-white">{amount}</p>
            {invoiceNo != null && invoiceNo !== "" && (
              <button type="button" onClick={p.onViewInvoice} className="min-h-[44px] text-sm font-medium text-[#C2410C] dark:text-[#FF8A6E] hover:underline">
                View invoice #{String(invoiceNo)}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        {isHost && status === "booking_request" && (
          <>
            <button type="button" className={primary} onClick={p.onIssueInvoice}>Issue invoice</button>
            <button type="button" className={secondary} onClick={p.onMessage}>Message guest</button>
          </>
        )}
        {isHost && status === "invoice_sent" && (
          <button type="button" className={secondary} disabled={p.isDeleting} onClick={() => setConfirming("delete")}>
            {p.isDeleting ? "Deleting..." : "Delete invoice"}
          </button>
        )}
        {!isHost && status === "invoice_sent" && (
          <button type="button" className={primary} onClick={p.onViewInvoice}>Check &amp; pay invoice</button>
        )}
        {!isHost && status === "booking_request" && (
          <button type="button" className={secondary} onClick={p.onMessage}>Message host</button>
        )}
        {status === "canceled" && (
          <button type="button" className={secondary} onClick={p.onMessage}>Open messages</button>
        )}
      </div>

      {!isHost && status === "invoice_sent" && (
        <p className="mt-4 text-xs text-neutral-500 dark:text-neutral-400">Payments are processed securely. You can pay by card or PayPal.</p>
      )}

      {(canCancel || (isHost && status === "booking_request")) && (
        <div className="mt-5 border-t border-neutral-200 dark:border-neutral-800 pt-3">
          <button type="button" className={dangerLink} disabled={p.isCanceling} onClick={() => setConfirming("cancel")}>
            {isHost ? "Reject request" : "Cancel booking"}
          </button>
        </div>
      )}

      {confirming && (
        <div role="alertdialog" aria-live="assertive" className="mt-4 rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
          <p className="text-sm text-neutral-900 dark:text-neutral-100">
            {confirming === "cancel"
              ? isHost ? "Reject this request?" : "Cancel this booking?"
              : "Delete this invoice? The booking will be canceled."}
          </p>
          <div className="mt-3 flex gap-2">
            <button type="button" className={secondary} onClick={() => setConfirming(null)}>Keep</button>
            <button
              type="button"
              className="inline-flex min-h-[48px] items-center rounded-xl bg-red-700 px-5 text-[15px] font-semibold text-white hover:bg-red-800"
              onClick={() => {
                const which = confirming;
                setConfirming(null);
                which === "cancel" ? p.onCancel() : p.onDeleteInvoice();
              }}
            >
              Yes, {confirming === "cancel" ? (isHost ? "reject" : "cancel") : "delete"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default NextStepCard;
